import type { IObjectImpl } from './interactions/ObjectImpl'
import type { Position } from '../types'

/**
 * Every registered object under the given client point, topmost first.
 *
 * Uses the live DOM stack (document.elementsFromPoint) rather than bbox math: it respects actual
 * z-order and clipping at any depth, and avoids re-deriving each nested object's accumulated parent
 * offset. Each object's <g> carries a transparent full-box rect (SelectableObject), so an object is
 * found anywhere in its box, not just on its ink.
 *
 * Restricted to nodes inside `svg` so a second poster mounted on the same page (the event page's
 * hidden capture poster) can't contribute ids. The handles layer sits on top and is hit first, but
 * carries no id of its own, so the registry check drops it.
 *
 * This is the whole of hit-testing now: with plain clicks no longer moving the selection, the two
 * bbox/DOM probes that used to redirect them (overlappingTopLevel, descendantAtPoint) are gone, and
 * Alt-click cycling walks this stack instead.
 */
export function objectsAtPoint(
	client:   Position,
	svg:      SVGSVGElement,
	elements: Record<string, IObjectImpl>,
): string[] {
	if (typeof document === 'undefined') return []
	const ids: string[] = []
	for (const node of document.elementsFromPoint(client.x, client.y)) {
		if (!svg.contains(node)) continue
		const withId = (node as Element).closest?.('[id]') as Element | null
		const id     = withId?.id
		if (!id || !elements[id] || ids.includes(id)) continue
		ids.push(id)
	}
	return ids
}
